/**
 * The markdown `LegalVersionTranslations.body` is written in — the ONE grammar
 * the admin editor writes and the mobile reader renders.
 *
 * It lives beside the schema because it is part of the contract: the schema says
 * the body is a string, this says which strings mean what. The subset is what a
 * published policy needs and nothing more — headings, paragraphs, bulleted and
 * numbered lists, bold runs. There is deliberately no italic, no link, no
 * escaping and no HTML: every client renders the blocks through its own
 * typography, so nothing has to be sanitized on read (U6, U7).
 */

export interface MarkdownSpan {
  text: string;
  isBold: boolean;
}

export type MarkdownBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; spans: MarkdownSpan[] }
  | { kind: 'paragraph'; spans: MarkdownSpan[] }
  | { kind: 'list'; items: MarkdownSpan[][] }
  | { kind: 'ordered-list'; items: MarkdownSpan[][] };

const HEADING = /^(#{1,3}) +(.*)$/u;
const BULLET = /^[-*] +(.*)$/u;
const ORDERED = /^\d+[.)] +(.*)$/u;
/** A pair of markers with at least one character between them. */
const BOLD = /\*\*([^*]+?)\*\*/gu;

/**
 * Splits one line's text into plain and bold spans.
 *
 * An **unclosed** marker stays literal on purpose: a published version is
 * immutable (N16), so a typo in it can never be corrected — and swallowing the
 * rest of the document into a bold run would turn a stray asterisk pair into an
 * unreadable page.
 */
function toSpans(text: string): MarkdownSpan[] {
  const spans: MarkdownSpan[] = [];
  let cursor = 0;

  BOLD.lastIndex = 0;
  let match = BOLD.exec(text);

  while (match !== null) {
    const [whole, inner = ''] = match;

    if (match.index > cursor) {
      spans.push({ text: text.slice(cursor, match.index), isBold: false });
    }
    spans.push({ text: inner, isBold: true });
    cursor = match.index + whole.length;

    match = BOLD.exec(text);
  }

  if (cursor < text.length) {
    spans.push({ text: text.slice(cursor), isBold: false });
  }

  return spans;
}

/** The lines of one paragraph or list item, joined the way a hard wrap means them. */
const joinWrapped = (lines: string[]): string => lines.join(' ').trim();

/**
 * Drops a document's own title when the surface is already showing it.
 *
 * The seeded corpus opens each body with `# <the version's title>`, and the
 * reader renders that title in its header — so the member met it twice, once
 * large and once larger. Deliberately conditional on the text MATCHING: a body
 * whose first heading says something else is saying something else, and
 * dropping it would delete content from an immutable version (N16).
 */
export function withoutLeadingTitle(body: string, title: string): string {
  const wanted = title.trim();
  if (wanted === '') return body;

  const lines = body.replace(/\r\n?/gu, '\n').split('\n');
  const first = lines.findIndex((line) => line.trim() !== '');
  if (first === -1) return body;

  const heading = HEADING.exec(lines[first]?.trim() ?? '');
  if (heading === null || heading[2]?.trim() !== wanted) return body;

  return lines.slice(first + 1).join('\n');
}

export function parseMarkdownBlocks(body: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];

  // Paragraph lines waiting for their terminator, and the open list beside them.
  // Exactly one of the two is ever non-empty: a list marker flushes the
  // paragraph and a paragraph line flushes the list.
  let paragraph: string[] = [];
  let items: string[][] = [];
  let listKind: 'list' | 'ordered-list' = 'list';

  const flushParagraph = (): void => {
    const text = joinWrapped(paragraph);
    paragraph = [];
    if (text !== '') blocks.push({ kind: 'paragraph', spans: toSpans(text) });
  };

  const flushList = (): void => {
    const collected = items;
    items = [];
    if (collected.length > 0) {
      blocks.push({
        kind: listKind,
        items: collected.map((lines) => toSpans(joinWrapped(lines))),
      });
    }
  };

  const flush = (): void => {
    flushParagraph();
    flushList();
  };

  const openItem = (kind: 'list' | 'ordered-list', text: string): void => {
    flushParagraph();
    // A bullet straight after a numbered item starts a new list, not a new item.
    if (listKind !== kind) flushList();
    listKind = kind;
    items.push([text]);
  };

  for (const raw of body.replace(/\r\n?/gu, '\n').split('\n')) {
    const line = raw.trim();

    if (line === '') {
      flush();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading !== null) {
      flush();
      const [, hashes = '', text = ''] = heading;
      blocks.push({
        kind: 'heading',
        level: hashes.length as 1 | 2 | 3,
        spans: toSpans(text),
      });
      continue;
    }

    const bullet = BULLET.exec(line);
    if (bullet !== null) {
      openItem('list', bullet[1] ?? '');
      continue;
    }

    const ordered = ORDERED.exec(line);
    if (ordered !== null) {
      openItem('ordered-list', ordered[1] ?? '');
      continue;
    }

    // An unmarked line while a list is open is that item's CONTINUATION — the
    // corpus indents wrapped bullets to line up under their text, and treating
    // the second line as a new paragraph would break the item in half.
    const open = items[items.length - 1];
    if (open !== undefined) {
      open.push(line);
      continue;
    }

    paragraph.push(line);
  }

  flush();

  return blocks;
}

/**
 * Bold spans back to `**markers**`, with neighbours of one style merged so the
 * editor's split text nodes do not come out as `**a****b**`.
 */
function fromSpans(spans: MarkdownSpan[]): string {
  const merged: MarkdownSpan[] = [];

  for (const span of spans) {
    if (span.text === '') continue;
    const last = merged[merged.length - 1];
    if (last !== undefined && last.isBold === span.isBold) {
      last.text += span.text;
    } else {
      merged.push({ ...span });
    }
  }

  return merged.map((span) => (span.isBold ? `**${span.text}**` : span.text)).join('');
}

/** The inverse of `parseMarkdownBlocks`: one line per paragraph, blocks a blank line apart. */
export function serializeMarkdownBlocks(blocks: readonly MarkdownBlock[]): string {
  return blocks
    .map((block) => {
      switch (block.kind) {
        case 'heading':
          return `${'#'.repeat(block.level)} ${fromSpans(block.spans)}`;
        case 'paragraph':
          return fromSpans(block.spans);
        case 'list':
          return block.items.map((item) => `- ${fromSpans(item)}`).join('\n');
        case 'ordered-list':
          return block.items.map((item, index) => `${index + 1}. ${fromSpans(item)}`).join('\n');
      }
    })
    .join('\n\n');
}
